import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import ApiService from '../../_services/ApiService';
import { openAttendanceForBatches } from '../../utils/attendance-nav';
import Config from '../../_config/config';
import DashboardDayView from './components/DashboardDayView';
import DashboardWeekView from './components/DashboardWeekView';
import DashboardMonthView from './components/DashboardMonthView';
import LessonPlanOverview from './components/LessonPlanOverview';
import DashboardAgendaView from './components/DashboardAgendaView';
import './styles.scss';

export interface UnifiedSlot {
  templateSlotId: number;
  actualSlotId: number | null;
  templateId: number;
  templateName: string;
  date: string;
  startTime: string;
  endTime: string;
  instructorId: number;
  instructorName: string;
  topicMasterId: number | null;
  topicName: string | null;
  agenda: string | null;
  courseName: string;
  batchNames: string[];
  batchIds: number[];
  status: 'planned' | 'pre-scheduled' | 'scheduled' | 'conducted' | 'cancelled';
  studentCount: number | null;
  presentCount: number | null;
  attendanceRate: number | null;
  lessonPlanId: number | null;
  lessonPlanName: string | null;
  lessonPlanStepCount: number | null;
  lessonPlanCompletedSteps: number | null;
  // Actual (conducted) slot data
  actualStartTime: string | null;
  actualEndTime: string | null;
  actualInstructorId: number | null;
  actualInstructorName: string | null;
  actualTopicId: number | null;
  actualTopicName: string | null;
  actualBatchNames: string[];
  actualBatchIds: number[];
  zoomMeetingId: string | null;
  // OnlineMeetingType code: 44 = ZOOM, 45 = TEAMS (mirrors Tuningfork
  // Constants.StudyResourceConstant). Null when no online meeting is configured.
  onlineMeetingType: number | null;
  // For Teams meetings the join URL is parsed server-side from ZoomMeetingData JSON.
  onlineJoinUrl: string | null;
  // Approval workflow (PSB B.1.a)
  confirmedByUserId: number | null;
  confirmedAt: string | null;
  templatePublishStatus: 'Draft' | 'Published' | 'Unpublished';
}

interface Analytics {
  totalPlanned: number;
  totalScheduled: number;
  totalConducted: number;
  totalCancelled: number;
  upcomingToday: number;
  upcomingThisWeek: number;
  totalHours: number;
  conductedHours: number;
  averageAttendance: number | null;
  lessonPlansCovered: number;
  lessonPlansTotal: number;
  lessonPlanCoveragePercent: number;
}

interface LessonPlanSummaryItem {
  lessonPlanId: number;
  lessonPlanName: string;
  topicId: number;
  topicName: string;
  totalSteps: number;
  completedSteps: number;
  batchId: number;
  batchName: string;
}

interface InstructorInfo {
  id: number;
  name: string;
  firstName: string | null;
  lastName: string | null;
  code: string | null;
}

type ViewMode = 'agenda' | 'day' | 'week' | 'month' | 'lessonplans';

function toDateKey(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${dd}`;
}

function parseLocalDate(str: string): Date {
  const [y, m, d] = str.split('-').map(Number);
  return new Date(y, m - 1, d);
}

const InstructorDashboard: React.FC = () => {
  const history = useHistory();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<ViewMode>('agenda');
  const [slots, setSlots] = useState<UnifiedSlot[]>([]);
  const [analytics, setAnalytics] = useState<Analytics | null>(null);
  const [lessonPlanSummary, setLessonPlanSummary] = useState<LessonPlanSummaryItem[]>([]);
  const [instructor, setInstructor] = useState<InstructorInfo | null>(null);

  // Date navigation
  const [currentDate, setCurrentDate] = useState(toDateKey(new Date()));
  const [currentMonth, setCurrentMonth] = useState(new Date(new Date().getFullYear(), new Date().getMonth(), 1));

  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState<string>('');
  const [filterDateRange, setFilterDateRange] = useState<{ start: string; end: string }>(() => {
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    return { start: toDateKey(yesterday), end: '' };
  });
  const [showAnalytics, setShowAnalytics] = useState(true);

  // Get instructor ID from localStorage
  const instructorId = useMemo(() => {
    try {
      const currentUser = JSON.parse(localStorage.getItem('currentUser') || '{}');
      return currentUser.employeeId || currentUser.instructorId || null;
    } catch {
      return null;
    }
  }, []);

  const fetchDashboard = useCallback(async () => {
    if (!instructorId) {
      setError('Could not determine instructor ID. Please log in again.');
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await ApiService.getInstructorDashboard(
        instructorId,
        filterDateRange.start || undefined,
        filterDateRange.end || undefined
      );

      const data = response?.data?.data || response?.data;
      if (data) {
        setSlots(data.slots || []);
        setAnalytics(data.analytics || null);
        setLessonPlanSummary(data.lessonPlanSummary || []);
        setInstructor(data.instructor || null);
      }
    } catch (err: any) {
      console.error('Failed to load instructor dashboard:', err);
      setError('Failed to load dashboard data. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [instructorId, filterDateRange.start, filterDateRange.end]);

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  // Approval workflow (PSB B.1.a) — instructor confirms / revokes confirmation
  // on their own slots. Refetch after either to pick up the new state.
  const handleConfirmSlot = useCallback(async (slot: UnifiedSlot) => {
    try {
      await ApiService.confirmActivitySlot(slot.templateId, slot.templateSlotId);
      fetchDashboard();
    } catch (err: any) {
      console.error('Failed to confirm slot:', err);
    }
  }, [fetchDashboard]);

  const handleUnconfirmSlot = useCallback(async (slot: UnifiedSlot) => {
    try {
      await ApiService.unconfirmActivitySlot(slot.templateId, slot.templateSlotId);
      fetchDashboard();
    } catch (err: any) {
      console.error('Failed to revoke confirmation:', err);
    }
  }, [fetchDashboard]);

  // Filter slots
  const filteredSlots = useMemo(() => {
    let result = [...slots];

    if (filterStatus) {
      result = result.filter(s => s.status === filterStatus);
    }

    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      result = result.filter(s =>
        (s.topicName || '').toLowerCase().includes(q) ||
        (s.instructorName || '').toLowerCase().includes(q) ||
        (s.templateName || '').toLowerCase().includes(q) ||
        (s.agenda || '').toLowerCase().includes(q) ||
        (s.courseName || '').toLowerCase().includes(q) ||
        s.batchNames.some(b => b.toLowerCase().includes(q))
      );
    }

    return result;
  }, [slots, filterStatus, searchQuery]);

  // ── Bulk confirmation (review & confirm many assigned slots at once) ──
  const [selectedSlotIds, setSelectedSlotIds] = useState<number[]>([]);
  const [bulkConfirming, setBulkConfirming] = useState(false);

  // Slots an instructor can still confirm: not yet confirmed and not cancelled.
  const confirmableSlots = useMemo(
    () => filteredSlots.filter(s => s.confirmedByUserId == null && s.status !== 'cancelled'),
    [filteredSlots]
  );
  const confirmableIds = useMemo(() => confirmableSlots.map(s => s.templateSlotId), [confirmableSlots]);

  // Drop selections that are no longer confirmable (filters changed / got confirmed).
  useEffect(() => {
    setSelectedSlotIds(prev => {
      const allowed = new Set(confirmableIds);
      const next = prev.filter(id => allowed.has(id));
      return next.length === prev.length ? prev : next;
    });
  }, [confirmableIds]);

  const toggleSlotSelection = useCallback((slotId: number) => {
    setSelectedSlotIds(prev =>
      prev.includes(slotId) ? prev.filter(id => id !== slotId) : [...prev, slotId]
    );
  }, []);

  const allConfirmableSelected = confirmableIds.length > 0 && selectedSlotIds.length === confirmableIds.length;
  const toggleSelectAllConfirmable = useCallback(() => {
    setSelectedSlotIds(prev => (prev.length === confirmableIds.length ? [] : [...confirmableIds]));
  }, [confirmableIds]);

  const handleBulkConfirm = useCallback(async () => {
    if (selectedSlotIds.length === 0) return;
    setBulkConfirming(true);
    try {
      await ApiService.bulkConfirmActivitySlots(selectedSlotIds);
      setSelectedSlotIds([]);
      fetchDashboard();
    } catch (err: any) {
      console.error('Failed to bulk-confirm slots:', err);
    } finally {
      setBulkConfirming(false);
    }
  }, [selectedSlotIds, fetchDashboard]);

  // Slots for current day
  const daySlots = useMemo(() => {
    return filteredSlots.filter(s => s.date === currentDate);
  }, [filteredSlots, currentDate]);

  // Slots for current week
  const weekSlots = useMemo(() => {
    const d = parseLocalDate(currentDate);
    const dayOfWeek = d.getDay();
    const mondayOffset = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
    const monday = new Date(d);
    monday.setDate(d.getDate() + mondayOffset);
    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 6);

    const monStr = toDateKey(monday);
    const sunStr = toDateKey(sunday);

    return filteredSlots.filter(s => s.date >= monStr && s.date <= sunStr);
  }, [filteredSlots, currentDate]);

  // Week bounds for week view
  const weekBounds = useMemo(() => {
    const d = parseLocalDate(currentDate);
    const dayOfWeek = d.getDay();
    const mondayOffset = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
    const monday = new Date(d);
    monday.setDate(d.getDate() + mondayOffset);
    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 6);
    return { start: toDateKey(monday), end: toDateKey(sunday) };
  }, [currentDate]);

  const handleDayChange = (date: string) => setCurrentDate(date);

  const handleWeekChange = (offset: number) => {
    const d = parseLocalDate(currentDate);
    d.setDate(d.getDate() + offset * 7);
    setCurrentDate(toDateKey(d));
  };

  const handleMonthChange = (month: Date) => setCurrentMonth(month);

  const handleDayClickFromMonth = (date: string) => {
    setCurrentDate(date);
    setViewMode('day');
  };

  // URL builder is shared with admin's TemplateConductedView via
  // utils/attendance-nav — see openAttendanceForBatches.
  const navigateToAttendance = openAttendanceForBatches;

  const navigateToLessonPlan = (batchId: number, lessonPlanId: number, topicId?: number, instructorId?: number, activitySlotTemplateId?: number) => {
    // Node-only tenants (env.js JAVA_BACKEND:"none") and local dev with
    // REACT_APP_DEFAULT_BACKEND=node use the node-backed /lesson-plan-node
    // route; everyone else stays on the Java /lessonplan page.
    const useNodeLessonPlan = !Config.hasJavaBackend || Config.defaultBackend === 'node';
    const base = useNodeLessonPlan ? '/lesson-plan-node' : '/lessonplan';
    let url = `${base}?batchId=${batchId}&lessonPlanId=${lessonPlanId}`;
    if (topicId) url += `&topicId=${topicId}`;
    if (instructorId) url += `&instructorId=${instructorId}`;
    if (activitySlotTemplateId) url += `&activitySlotTemplateId=${activitySlotTemplateId}`;
    window.open(url, '_blank');
  };

  const navigateToTemplate = (templateId: number) => {
    window.open(`/schedules-templates/${templateId}`, '_blank');
  };

  const handleGoToToday = () => {
    setCurrentDate(toDateKey(new Date()));
    setCurrentMonth(new Date(new Date().getFullYear(), new Date().getMonth(), 1));
  };

  if (loading) {
    return (
      <div className="instructor-dashboard">
        <div className="idash-loading">
          <div className="idash-spinner" />
          <p>Loading your teaching plan...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="instructor-dashboard">
        <div className="idash-error">
          <p>{error}</p>
          <button onClick={fetchDashboard} className="idash-retry-btn">Retry</button>
        </div>
      </div>
    );
  }

  return (
    <div className="instructor-dashboard">
      {/* Header */}
      <div className="idash-header">
        <div className="idash-header-left">
          <h1 className="idash-title">My Teaching Plan</h1>
          {instructor && (
            <span className="idash-instructor-name">
              {instructor.name || `${instructor.firstName || ''} ${instructor.lastName || ''}`.trim()}
            </span>
          )}
        </div>
        <div className="idash-header-actions">
          <button className="idash-today-btn" onClick={handleGoToToday}>
            Today
          </button>
          <button className="idash-refresh-btn" onClick={fetchDashboard}>
            Refresh
          </button>
        </div>
      </div>

      {/* Analytics Cards */}
      {analytics && (
        <div className="idash-analytics-section">
          <button
            className="idash-analytics-toggle"
            onClick={() => setShowAnalytics(!showAnalytics)}
          >
            {showAnalytics ? 'Hide Analytics' : 'Show Analytics'}
          </button>
          {showAnalytics && (
            <div className="idash-analytics-grid">
              <div className="idash-analytics-card purple">
                <div className="card-value">{analytics.upcomingToday}</div>
                <div className="card-label">Today's Classes</div>
              </div>
              <div className="idash-analytics-card blue">
                <div className="card-value">{analytics.upcomingThisWeek}</div>
                <div className="card-label">This Week</div>
              </div>
              <div className="idash-analytics-card green">
                <div className="card-value">{analytics.conductedHours}h</div>
                <div className="card-label">Hours Taught</div>
              </div>
              <div className="idash-analytics-card orange">
                <div className="card-value">{analytics.averageAttendance !== null ? `${analytics.averageAttendance}%` : '-'}</div>
                <div className="card-label">Attendance Rate</div>
              </div>
              <div className="idash-analytics-card teal">
                <div className="card-value">{analytics.lessonPlansCovered}/{analytics.lessonPlansTotal}</div>
                <div className="card-label">Lesson Plans</div>
              </div>
              <div className="idash-analytics-card grey">
                <div className="card-value">{analytics.totalPlanned + analytics.totalScheduled}</div>
                <div className="card-label">Upcoming</div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Toolbar: View selector + Search + Filters */}
      <div className="idash-toolbar">
        <div className="idash-view-selector">
          {(['agenda', 'day', 'week', 'month', 'lessonplans'] as ViewMode[]).map(mode => (
            <button
              key={mode}
              className={`idash-view-btn ${viewMode === mode ? 'active' : ''}`}
              onClick={() => setViewMode(mode)}
            >
              {mode === 'agenda' ? 'Agenda' : mode === 'day' ? 'Day' : mode === 'week' ? 'Week' : mode === 'month' ? 'Month' : 'Lesson Plans'}
            </button>
          ))}
        </div>

        <div className="idash-toolbar-right">
          <input
            type="text"
            className="idash-search"
            placeholder="Search slots..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
          />
          <select
            className="idash-status-filter"
            value={filterStatus}
            onChange={e => setFilterStatus(e.target.value)}
          >
            <option value="">All Statuses</option>
            <option value="planned">Planned</option>
            <option value="pre-scheduled">Pre-Scheduled</option>
            <option value="scheduled">Scheduled</option>
            <option value="conducted">Conducted</option>
            <option value="cancelled">Cancelled</option>
          </select>
          <div className="idash-date-filters">
            <input
              type="date"
              className="idash-date-input"
              value={filterDateRange.start}
              onChange={e => setFilterDateRange(prev => ({ ...prev, start: e.target.value }))}
              placeholder="From"
            />
            <input
              type="date"
              className="idash-date-input"
              value={filterDateRange.end}
              onChange={e => setFilterDateRange(prev => ({ ...prev, end: e.target.value }))}
              placeholder="To"
            />
          </div>
        </div>
      </div>

      {/* Status legend */}
      <div className="idash-legend">
        <span className="legend-item planned">Planned</span>
        <span className="legend-item pre-scheduled">Pre-Scheduled</span>
        <span className="legend-item scheduled">Scheduled</span>
        <span className="legend-item conducted">Conducted</span>
        <span className="legend-item cancelled">Cancelled</span>
      </div>

      {/* Bulk confirmation bar — review & confirm many assigned slots at once */}
      {confirmableSlots.length > 0 && (
        <div className="idash-bulk-confirm-bar">
          <label className="idash-bulk-select-all">
            <input
              type="checkbox"
              checked={allConfirmableSelected}
              onChange={toggleSelectAllConfirmable}
            />
            Select all unconfirmed ({confirmableSlots.length})
          </label>
          <span className="idash-bulk-count">{selectedSlotIds.length} selected</span>
          {selectedSlotIds.length > 0 && (
            <button className="idash-bulk-clear" onClick={() => setSelectedSlotIds([])}>
              Clear
            </button>
          )}
          <button
            className="idash-bulk-confirm-btn"
            onClick={handleBulkConfirm}
            disabled={selectedSlotIds.length === 0 || bulkConfirming}
          >
            {bulkConfirming ? 'Confirming…' : `Confirm Selected (${selectedSlotIds.length})`}
          </button>
        </div>
      )}

      {/* View Content */}
      <div className="idash-content">
        {viewMode === 'agenda' && (
          <DashboardAgendaView
            slots={filteredSlots}
            onDayClick={handleDayClickFromMonth}
            onAttendance={navigateToAttendance}
            onLessonPlan={navigateToLessonPlan}
            onViewTemplate={navigateToTemplate}
            onConfirm={handleConfirmSlot}
            onUnconfirm={handleUnconfirmSlot}
            onMeetingStarted={fetchDashboard}
            selectedSlotIds={selectedSlotIds}
            onToggleSelect={toggleSlotSelection}
          />
        )}
        {viewMode === 'day' && (
          <DashboardDayView
            slots={daySlots}
            date={currentDate}
            onDateChange={handleDayChange}
            onAttendance={navigateToAttendance}
            onLessonPlan={navigateToLessonPlan}
            onViewTemplate={navigateToTemplate}
            onConfirm={handleConfirmSlot}
            onUnconfirm={handleUnconfirmSlot}
          />
        )}
        {viewMode === 'week' && (
          <DashboardWeekView
            slots={weekSlots}
            currentDate={currentDate}
            weekStart={weekBounds.start}
            weekEnd={weekBounds.end}
            onWeekChange={handleWeekChange}
            onDayClick={handleDayClickFromMonth}
            onAttendance={navigateToAttendance}
            onLessonPlan={navigateToLessonPlan}
            onViewTemplate={navigateToTemplate}
            onConfirm={handleConfirmSlot}
            onUnconfirm={handleUnconfirmSlot}
          />
        )}
        {viewMode === 'month' && (
          <DashboardMonthView
            slots={filteredSlots}
            currentMonth={currentMonth}
            onMonthChange={handleMonthChange}
            onDayClick={handleDayClickFromMonth}
          />
        )}
        {viewMode === 'lessonplans' && (
          <LessonPlanOverview
            lessonPlanSummary={lessonPlanSummary}
            onOpenLessonPlan={navigateToLessonPlan}
          />
        )}
      </div>
    </div>
  );
};

export default InstructorDashboard;
